import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { notificationService } from '../services/NotificationService';
import { useAuth } from '../contexts/AuthContext';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '../services/stripe/config';
import StripePaymentForm from './StripePaymentForm';
import DepositValidationDialog from './DepositValidationDialog';
import {
  CreditCard,
  AccountBalance,
  Security,
  CheckCircle,
  Cancel,
  Payment,
  LocalShipping,
  CalendarToday,
  Euro,
  LocationOn,
  Info
} from '@mui/icons-material';

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  dressData: {
    title: string;
    price: number;
    location: string;
    owner: string;
    deposit: number;
  };
  bookingData: {
    startDate: string;
    endDate: string;
    deliveryMethod: string;
    specialRequests: string;
  };
  onPaymentSuccess: (paymentData: any) => void;
}

interface PaymentMethod {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  processingFee: number;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  dressData,
  bookingData,
  onPaymentSuccess
}) => {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    email: '',
    phone: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [depositValidated, setDepositValidated] = useState(false);
  const [openDepositDialog, setOpenDepositDialog] = useState(false);

  const paymentMethods: PaymentMethod[] = [
    {
      id: 'stripe',
      name: 'Carte bancaire',
      icon: <CreditCard />,
      description: 'Visa, Mastercard, American Express',
      processingFee: 0
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: <AccountBalance />,
      description: 'Paiement sécurisé via PayPal',
      processingFee: 0
    }
  ];

  const calculateDuration = () => {
    if (!bookingData.startDate || !bookingData.endDate) return 0;
    const start = new Date(bookingData.startDate);
    const end = new Date(bookingData.endDate);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  };

  const calculateTotal = () => {
    const duration = calculateDuration();
    
    // Prix fixe par location, peu importe la durée
    const basePrice = dressData.price;
    
    const deliveryFee = bookingData.deliveryMethod === 'delivery' ? 15 : 0;
    const deposit = dressData.deposit; // Caution définie par le propriétaire
    const selectedMethod = paymentMethods.find(m => m.id === selectedPaymentMethod);
    const processingFee = selectedMethod?.processingFee || 0;
    
    // Montant total bloqué (location + caution)
    const totalBlocked = basePrice + deliveryFee + deposit + processingFee;
    
    return {
      basePrice,
      deliveryFee,
      deposit,
      processingFee,
      total: totalBlocked,
      // Montants qui seront versés après confirmation mutuelle
      ownerAmount: basePrice + deliveryFee, // Le propriétaire reçoit la location + frais
      renterRefund: deposit // Le locataire récupère sa caution
    };
  };

  const handlePaymentMethodSelect = (methodId: string) => {
    setSelectedPaymentMethod(methodId);
    setActiveStep(1);
  };

  const handleDepositValidation = (accepted: boolean) => {
    if (accepted) {
      setDepositValidated(true);
      setOpenDepositDialog(false);
    } else {
      // Si le dépôt est refusé, annuler le processus
      setOpenDepositDialog(false);
      onClose();
    }
  };

  const handlePaymentSubmit = async () => {
    // Vérifier que la caution a été validée
    if (!depositValidated && dressData.deposit > 0) {
      setOpenDepositDialog(true);
      return;
    }

    setIsProcessing(true);
    
    // Simulation du processus de paiement
    setTimeout(async () => {
      setIsProcessing(false);
      setPaymentStatus('success');
      setActiveStep(2);
      
      // Simulation du blocage automatique du montant
      const paymentResult = {
        transactionId: `TXN_${Date.now()}`,
        amount: calculateTotal().total,
        method: selectedPaymentMethod,
        status: 'blocked',
        blockedUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 jours
        bookingId: `BOOK_${Date.now()}`,
        depositValidated: depositValidated
      };
      
      // Envoyer les notifications
      if (user) {
        await notificationService.notifyPaymentSuccess(
          user.uid,
          dressData.title,
          paymentResult.amount,
          paymentResult.bookingId
        );
      }

      // Programmer un rappel de retour
      if (user) {
        await notificationService.scheduleReturnReminder(
          paymentResult.bookingId,
          user.uid,
          bookingData.endDate,
          dressData.title
        );
      }
      
      onPaymentSuccess(paymentResult);
    }, 3000);
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedPaymentMethod('');
    setPaymentData({
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardholderName: '',
      email: '',
      phone: ''
    });
    setPaymentStatus('pending');
    onClose();
  };

  const steps = ['Méthode de paiement', 'Informations de paiement', 'Confirmation'];

  const totals = calculateTotal();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Payment sx={{ mr: 1, color: '#a5816d' }} />
          Paiement sécurisé
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Étape 1: Sélection de la méthode de paiement */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Choisissez votre méthode de paiement
            </Typography>
            
            <Grid container spacing={2}>
              {paymentMethods.map((method) => (
                <Grid item xs={12} sm={6} key={method.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      border: selectedPaymentMethod === method.id ? '2px solid #a5816d' : '1px solid #e0e0e0',
                      '&:hover': { borderColor: '#a5816d' }
                    }}
                    onClick={() => handlePaymentMethodSelect(method.id)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ color: '#a5816d', mr: 1 }}>
                          {method.icon}
                        </Box>
                        <Typography variant="h6">{method.name}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {method.description}
                      </Typography>
                      {method.processingFee > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Frais: {method.processingFee}€
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Étape 2: Paiement Stripe */}
        {activeStep === 1 && selectedPaymentMethod === 'stripe' && (
          <Elements stripe={stripePromise}>
            <StripePaymentForm
              paymentData={{
                bookingId: `booking_${Date.now()}`,
                ownerId: 'owner_123',
                renterId: 'renter_456',
                dressTitle: dressData.title,
                totalAmount: totals.total,
                ownerAmount: totals.ownerAmount,
                renterRefund: totals.renterRefund,
                currency: 'eur'
              }}
              onPaymentSuccess={onPaymentSuccess}
              onPaymentError={(error) => {
                console.error('Erreur de paiement:', error);
                setPaymentStatus('error');
              }}
            />
          </Elements>
        )}

        {/* Étape 2: PayPal */}
        {activeStep === 1 && selectedPaymentMethod === 'paypal' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Paiement PayPal
            </Typography>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AccountBalance sx={{ fontSize: 64, color: '#0070ba', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Redirection vers PayPal
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vous serez redirigé vers PayPal pour finaliser votre paiement
              </Typography>
            </Box>
          </Box>
        )}

        {/* Étape 3: Confirmation */}
        {activeStep === 2 && (
          <Box>
            {paymentStatus === 'success' && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  ✅ Paiement confirmé !
                </Typography>
                <Typography variant="body2">
                  Votre réservation a été confirmée et le montant a été bloqué automatiquement.
                </Typography>
              </Alert>
            )}

            <Paper sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
              <Typography variant="h6" gutterBottom>
                Résumé de votre réservation
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {dressData.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Propriétaire: {dressData.owner}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <LocationOn sx={{ fontSize: 16, mr: 0.5 }} />
                  {dressData.location}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Période:</Typography>
                  <Typography>{bookingData.startDate} - {bookingData.endDate}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Durée:</Typography>
                  <Typography>{calculateDuration()} jour(s)</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Livraison:</Typography>
                  <Typography>
                    {bookingData.deliveryMethod === 'delivery' ? 'Livraison à domicile' : 'Récupération sur place'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Location ({calculateDuration()} jour{calculateDuration() > 1 ? 's' : ''}):</Typography>
                  <Typography>{totals.basePrice}€</Typography>
                </Box>
                {totals.deliveryFee > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Frais de livraison:</Typography>
                    <Typography>{totals.deliveryFee}€</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography>Caution (bloquée):</Typography>
                    <Tooltip title="La caution ne sera pas payée mais bloquée sur votre compte. Nous nous réservons le droit de prélever l'argent en cas de problème.">
                      <Info sx={{ fontSize: 16, ml: 0.5, color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  <Typography>{totals.deposit}€</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6" sx={{ color: '#a5816d', fontWeight: 'bold' }}>
                    {totals.total}€
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <Security sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                <strong>Système d'escrow :</strong> Le montant total ({totals.total}€) est bloqué sur la plateforme. La caution ({totals.deposit}€) ne sera pas payée mais bloquée sur votre compte. Nous nous réservons le droit de prélever l'argent en cas de problème.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Résumé des coûts (toujours visible) */}
        {activeStep < 2 && (
          <Paper sx={{ p: 2, mt: 3, backgroundColor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
              Résumé des coûts
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Location ({calculateDuration()} jour{calculateDuration() > 1 ? 's' : ''}):</Typography>
              <Typography>{totals.basePrice}€</Typography>
            </Box>
            {totals.deliveryFee > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Livraison:</Typography>
                <Typography>{totals.deliveryFee}€</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Caution (escrow):</Typography>
              <Typography>{totals.deposit}€</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6">Total:</Typography>
              <Typography variant="h6" sx={{ color: '#a5816d', fontWeight: 'bold' }}>
                {totals.total}€
              </Typography>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {activeStep === 2 ? 'Fermer' : 'Annuler'}
        </Button>
        {activeStep === 0 && (
          <Button 
            variant="contained" 
            disabled={!selectedPaymentMethod}
            onClick={() => setActiveStep(1)}
            sx={{ backgroundColor: '#a5816d', '&:hover': { backgroundColor: '#8B7355' } }}
          >
            Continuer
          </Button>
        )}
        {activeStep === 1 && (
          <Button 
            variant="contained" 
            onClick={handlePaymentSubmit}
            disabled={isProcessing || !paymentData.email}
            sx={{ backgroundColor: '#a5816d', '&:hover': { backgroundColor: '#8B7355' } }}
          >
            {isProcessing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Traitement...
              </>
            ) : (
              'Payer et réserver'
            )}
          </Button>
        )}
      </DialogActions>

      {/* Dialog de validation de caution */}
      {dressData.deposit > 0 && (
        <DepositValidationDialog
          open={openDepositDialog}
          onClose={() => setOpenDepositDialog(false)}
          depositAmount={dressData.deposit}
          dressTitle={dressData.title}
          onValidate={handleDepositValidation}
        />
      )}
    </Dialog>
  );
};

export default PaymentDialog;
